import React, { useState, useRef, Children, cloneElement, lazy } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Users, CheckCircle2, Anchor, Waves, Sun, Fish, Camera, Compass, MessageCircle, ArrowRight, Instagram, Mail, Star, Share2 } from 'lucide-react';
import { Button } from '../components/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/Accordion';
// --- CONFIGURATION ---
// Users can easily edit these values
const CONFIG = {
  whatsappNumber: '5491122836110',
  instagramUrl: 'https://instagram.com/buceoynavegacion',
  email: 'info@buceoynavegacion.com',
  googleReviewsUrl: 'https://www.google.com/maps/place/Buceo+y+Navegaci%C3%B3n/@-34.5438357,-58.5536957,17z/data=!4m8!3m7!1s0x95bcb104909acf6f:0xf2823f46ec57e814!8m2!3d-34.5438357!4d-58.5511208!9m1!1b1!16s%2Fg%2F11xgpy4x_2?hl=en&entry=ttu&g_ep=EgoyMDI2MDYwMS4wIKXMDSoASAFQAw%3D%3D',
  paymentUrl: '#',
  // Checkouts específicos por tipo de participante (conectar luego)
  checkoutDiverUrl: 'https://checkout.librofy.online/cart/53353351479659:1',
  checkoutCompanionUrl: 'https://checkout.librofy.online/cart/53353482584427:1',
  totalSpots: 12,
  spotsLeft: 4,
  instructorPadiNumber: '397148',
  dates: 'Del 30 de agosto al 6 de septiembre, 2026',
  priceDiver: 1490,
  priceNonDiver: 1290,
  depositAmount: 100
};
// --- IMAGES ---
// Replace these URLs with real photos later
const IMAGES = {
  heroPoster: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=2070&auto=format&fit=crop',
  whyBonaire: ['https://cdn.magicpatterns.com/uploads/ddWYyZGc1cbT7vDTF6Lu9C/BONAIRE_BUCEO.jpg', 'https://cdn.magicpatterns.com/uploads/mDcE9tdrVpQzgiNs4LpjAu/BOANRE_WEB.jpg', 'https://cdn.magicpatterns.com/uploads/uCX9G6AFadcGg3nPhchzHZ/BONAIRE_WEB_2.jpg', 'https://cdn.magicpatterns.com/uploads/tzpWFkWFMxQpUJUMhRA2vk/BONAIRE_WEB_1.jpg', 'https://cdn.magicpatterns.com/uploads/spCRyFJr2bz1zTQCXag1d9/BONAIRE_7.jpg', 'https://cdn.magicpatterns.com/uploads/dhD63ut6gFFSHsQWpGU7n9/BONAIRE_WEB_34.jpg', 'https://cdn.magicpatterns.com/uploads/mmnpsazbRX6RzvpfdSLebZ/BONAIRE_NUEVA_WEB_3.jpg', 'https://cdn.magicpatterns.com/uploads/kpx4V596pg5A1asFbJSAtt/BONAIRE_6.jpg', 'https://cdn.magicpatterns.com/uploads/1rMziBFVXQW4HfTXCSfbsA/BONAIRE_4.jpg', 'https://cdn.magicpatterns.com/uploads/79riNU4pKJFMn72vUzi1W6/BONAIRE_NUEVA_WEB.jpg', 'https://cdn.magicpatterns.com/uploads/jkTahp1FujZ3XNkm7AvjzB/BONAIRE_WEB_14.webp', 'https://cdn.magicpatterns.com/uploads/qgzKLCc1V2QRaRJUg5TWfR/BONAIRE_WEB_12.webp' // Alojamiento (habitación)
  ],
  accommodation: {
    exterior: 'https://cdn.magicpatterns.com/uploads/uH3y6Vi1hJfNtdqaYnbg9j/Grand2BWindsock2BBonaire-2Bed-DeluxeVilla-135-1920w.webp',
    terraza: 'https://cdn.magicpatterns.com/uploads/4sXUa7EPzHXoZfxk4AsvPU/Grand2BWindsock2BBonaire-2Bed-DeluxeVilla-146-1920w.webp',
    living: 'https://cdn.magicpatterns.com/uploads/jjVbsrs76hPAEGsdUySWot/Grand2BWindsock2BBonaire-2Bed-DeluxeVilla-1001-1920w.webp',
    habitacion: 'https://cdn.magicpatterns.com/uploads/81arspwLQ5AkxM87bypAPo/Grand2BWindsock2BBonaire-2Bed-DeluxeVilla-IMG_1026-1920w.webp',
    bano: 'https://cdn.magicpatterns.com/uploads/tmVkrYQDQxasqtNmGedipz/Master2BBathroom-8a7e53aa-1920w.webp'
  },
  finalCta: 'https://images.unsplash.com/photo-1559494007-9f5847c49d94?q=80&w=1974&auto=format&fit=crop',
  // Logo oficial con fondo transparente — se muestra exactamente como fue subido
  logo: 'https://cdn.magicpatterns.com/uploads/41utpxk4CzbQZEoYHMxqpe/logo_sin_fondo.png'
};
// --- ANIMATION VARIANTS ---
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function Home() {
  const whatsappLink = `https://wa.me/${CONFIG.whatsappNumber}?text=Hola!%20Quiero%20información%20sobre%20el%20viaje%20a%20Bonaire%202026.`;
  const [packageType, setPackageType] = useState<'diver' | 'companion'>('diver');
  const scrollToParticipante = () => {
    document.getElementById('elegi-participante')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const galleryRef = useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const handleGalleryScroll = () => {
    const el = galleryRef.current;
    if (!el) return;
    const slide = el.querySelector<HTMLElement>('[data-slide]');
    if (!slide) return;
    const step = slide.offsetWidth + 16; // ancho del slide + gap
    setActiveSlide(Math.round(el.scrollLeft / step));
  };
  const goToSlide = (i: number) => {
    const el = galleryRef.current;
    if (!el) return;
    const slide = el.querySelector<HTMLElement>('[data-slide]');
    if (!slide) return;
    const step = slide.offsetWidth + 16;
    el.scrollTo({
      left: i * step,
      behavior: 'smooth'
    });
  };
  const handleShare = async () => {
    const shareData = {
      title: 'Viaje de Buceo a Bonaire | Buceo y Navegación',
      text: 'Descubrí uno de los mejores destinos de buceo del Caribe junto a Buceo y Navegación. Información completa, fechas, alojamiento y experiencia grupal.',
      url: window.location.href
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert('¡Enlace copiado! Ya podés compartirlo.');
      }
    } catch {
      // El usuario canceló o no se pudo compartir; no hacemos nada.
    }
  };
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-teal-500 selection:text-white" data-id="element-2">
      {/* FLOATING BUTTONS */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 lg:gap-4" data-id="element-3">
        <a href={whatsappLink} target="_blank" rel="noreferrer" className="group flex items-center justify-center w-14 h-14 bg-green-500 text-white rounded-full shadow-lg hover:bg-green-600 transition-all hover:scale-105 lg:order-2" data-id="element-4">
          <MessageCircle className="w-6 h-6" data-id="element-5" />
        </a>
        <button type="button" onClick={handleShare} aria-label="Compartir esta página" title="Compartir" className="flex items-center justify-center w-14 h-14 bg-slate-800 text-white rounded-full shadow-lg hover:bg-slate-700 transition-all hover:scale-105 lg:order-1" data-id="element-6">
          <Share2 className="w-6 h-6" data-id="element-7" />
        </button>
        <Button size="lg" className="shadow-lg bg-teal-500 hover:bg-teal-600 text-white rounded-full px-6 font-semibold hidden md:flex lg:order-3" onClick={scrollToParticipante} data-id="element-8">
          🤿 Quiero sumarme al grupo
        </Button>
      </div>

      {/* HEADER / NAV — Marca tipográfica premium */}
      <header className="absolute top-0 left-0 w-full z-40 px-6 py-4 flex justify-between items-center" data-id="element-9">
        <div className="leading-tight" data-id="element-10">
          <p className="text-white font-semibold uppercase tracking-[0.18em] md:tracking-[0.24em] text-base md:text-2xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]" data-id="element-11">
            Buceo y Navegación
          </p>
          <div className="mt-1.5 space-y-0.5 text-[9px] md:text-[11px] uppercase tracking-[0.22em] text-teal-300 leading-relaxed" data-id="element-12">
            <p data-id="element-13">Escuela de Buceo</p>
            <p data-id="element-14">Viajes Grupales</p>
            <p data-id="element-15">Agencia de Turismo HC 26.614</p>
          </div>
        </div>
        <div className="flex gap-4" data-id="element-16">
          <a href={whatsappLink} target="_blank" rel="noreferrer" className="text-white hover:text-teal-400 transition-colors bg-slate-900/40 p-2 rounded-full backdrop-blur-sm" data-id="element-17">
            <MessageCircle className="w-5 h-5" data-id="element-18" />
          </a>
          <a href={CONFIG.instagramUrl} target="_blank" rel="noreferrer" className="text-white hover:text-teal-400 transition-colors bg-slate-900/40 p-2 rounded-full backdrop-blur-sm" data-id="element-19">
            <Instagram className="w-5 h-5" data-id="element-20" />
          </a>
          <a href={`mailto:${CONFIG.email}`} className="text-white hover:text-teal-400 transition-colors bg-slate-900/40 p-2 rounded-full backdrop-blur-sm" data-id="element-21">
            <Mail className="w-5 h-5" data-id="element-22" />
          </a>
        </div>
      </header>

      {/* SECCIÓN 1 - HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-900" data-id="element-23">
        {/* Video Placeholder - Drop <video> tag here later (use poster={IMAGES.heroPoster}) */}
        <div className="absolute inset-0 z-0" data-id="element-24">
          <img src={IMAGES.heroPoster} alt="Bonaire" className="w-full h-full object-cover" data-id="element-25" />
          {/* Stronger dark overlays for maximum text legibility */}
          <div className="absolute inset-0 bg-slate-950/70" data-id="element-26" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/40 to-slate-950/90" data-id="element-27" />
        </div>

        <div className="relative z-10 container mx-auto px-6 text-center text-white pt-44 md:pt-52 pb-16" data-id="element-28">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-4xl mx-auto flex flex-col items-center" data-id="element-29">
            {/* Premium exclusivity badges — inicio de la propuesta de valor */}
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-4" data-id="element-30">
              {['Viaje Grupal Exclusivo'].map(label => <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-teal-400/40 bg-teal-400/10 px-3 py-1.5 text-xs sm:text-sm font-semibold uppercase tracking-wider text-teal-200 backdrop-blur-md" data-id="element-31">
                  <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-300" data-id="element-32" />
                  {label}
                </span>)}
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-6xl md:text-8xl font-extrabold mb-5 tracking-tight uppercase drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]" data-id="element-33">
              Bonaire <span className="text-teal-400" data-id="element-34">2026</span>
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-xl md:text-3xl font-light mb-6 text-white/90 max-w-2xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]" data-id="element-35">
              Uno de los destinos de buceo más increíbles del mundo.
            </motion.p>

            <motion.p variants={fadeInUp} className="text-base md:text-lg font-semibold mb-3 text-teal-200 uppercase tracking-wide" data-id="element-36">
              Viaje grupal exclusivo organizado por Buceo y Navegación.
            </motion.p>

            <motion.div variants={fadeInUp} className="mb-10 space-y-2" data-id="element-37">
              <p className="text-sm md:text-base text-white/75 tracking-wide" data-id="element-38">
                Del 30 de agosto al 6 de septiembre
              </p>
              <p className="text-lg md:text-2xl font-semibold text-teal-300 tracking-wide" data-id="element-39">
                Grupo reducido de {CONFIG.totalSpots} buceadores
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto" data-id="element-40">
              <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-base md:text-lg font-bold uppercase tracking-wide px-8 h-14 rounded-full shadow-xl shadow-teal-500/20" onClick={scrollToParticipante} data-id="element-41">
                🤿 Quiero sumarme al grupo
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 text-base md:text-lg font-bold uppercase tracking-wide px-8 h-14 rounded-full bg-white/5 backdrop-blur-sm" onClick={() => window.open(whatsappLink, '_blank')} data-id="element-42">
                <MessageCircle className="w-5 h-5 mr-2" data-id="element-43" />
                Hablar por WhatsApp
              </Button>
            </motion.div>

            {/* Urgency / spots-left block (edit CONFIG.spotsLeft) */}
            <motion.div variants={fadeInUp} className="mt-8 inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-5 py-2.5 backdrop-blur-md" data-id="element-44">
              <span className="text-lg" role="img" aria-label="urgencia" data-id="element-45">
                🔥
              </span>
              <span className="text-sm md:text-base font-bold text-amber-200" data-id="element-46">
                Quedan {CONFIG.spotsLeft} lugares disponibles
              </span>
            </motion.div>
            <motion.p variants={fadeInUp} className="mt-3 text-xs md:text-sm text-white/70" data-id="element-47">
              Una vez completado el grupo no se aceptarán nuevas reservas.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* SECCIÓN 2 - ¿POR QUÉ BONAIRE? */}
      <section className="py-24 bg-white" data-id="element-48">
        <div className="container mx-auto px-6" data-id="element-49">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-16" data-id="element-50">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-id="element-51">
              ¿Por qué tantos buzos sueñan con conocer Bonaire?
            </h2>
            <p className="text-lg text-slate-600" data-id="element-52">
              Conocida como la capital mundial del buceo de costa, Bonaire
              ofrece una libertad sin igual.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-53">
            {[{
            icon: <Waves data-id="element-54" />,
            title: 'Shore diving ilimitado',
            desc: 'Libertad total para bucear a tu ritmo, 24/7.'
          }, {
            icon: <Fish data-id="element-55" />,
            title: 'Arrecifes espectaculares',
            desc: 'Vida marina abundante y corales prístinos a pocos metros de la costa.'
          }, {
            icon: <Sun data-id="element-56" />,
            title: 'Visibilidad excepcional',
            desc: 'Aguas cristalinas con visibilidad que a menudo supera los 30 metros.'
          }, {
            icon: <Anchor data-id="element-57" />,
            title: 'Klein Bonaire',
            desc: 'Inmersiones embarcadas en la isla deshabitada frente a la costa.'
          }, {
            icon: <CheckCircle2 data-id="element-58" />,
            title: 'Nitrox incluido',
            desc: 'Buceá más seguro y por más tiempo con aire enriquecido.'
          }, {
            icon: <Compass data-id="element-59" />,
            title: 'Agua cálida',
            desc: 'Temperaturas agradables todo el año, ideal para bucear con traje fino.'
          }].map((feature, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:shadow-lg transition-shadow" data-id="element-60">
                <div className="w-14 h-14 bg-teal-100 text-teal-600 rounded-2xl flex items-center justify-center mb-6" data-id="element-61">
                  {cloneElement(feature.icon as React.ReactElement, {
                className: 'w-7 h-7'
              })}
                </div>
                <h3 className="text-xl font-bold mb-3 text-slate-900" data-id="element-62">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-63">{feature.desc}</p>
              </motion.div>)}
          </div>

          {/* Galería destino — swipeable, mobile-first (estilo TourRadar) */}
          <div className="mt-16 -mx-6 md:mx-0" data-id="element-64">
            <div ref={galleryRef} onScroll={handleGalleryScroll} className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-6 md:px-0 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-65">
              {IMAGES.whyBonaire.map((img, i) => <div key={i} data-slide className="snap-center shrink-0 w-[82%] sm:w-[60%] md:w-[42%]" data-id="element-66">
                  <div className="rounded-3xl overflow-hidden aspect-[4/3] md:aspect-[3/2]" data-id="element-67">
                    <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" data-id="element-68" />
                  </div>
                </div>)}
            </div>

            {/* Paginación elegante */}
            <div className="flex justify-center gap-2 mt-6" data-id="element-69">
              {IMAGES.whyBonaire.map((_, i) => <button key={i} type="button" onClick={() => goToSlide(i)} aria-label={`Ir a la imagen ${i + 1}`} className={`h-2 rounded-full transition-all ${activeSlide === i ? 'w-6 bg-teal-500' : 'w-2 bg-slate-300 hover:bg-slate-400'}`} data-id="element-70" />)}
            </div>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="mt-12 flex justify-center" data-id="element-71">
            <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-base md:text-lg font-bold uppercase tracking-wide px-8 h-14 rounded-full shadow-xl shadow-teal-500/20" onClick={scrollToParticipante} data-id="element-72">
              🤿 Quiero sumarme al grupo
            </Button>
          </motion.div>
        </div>
      </section>

      {/* SECCIÓN 3 - QUÉ INCLUYE */}
      <section className="py-24 bg-slate-900 text-white" data-id="element-73">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-74">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-75">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-76">
              ¿Qué incluye el viaje?
            </h2>
            <p className="text-lg text-slate-300" data-id="element-77">
              Todo resuelto para que solo te preocupes por disfrutar.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-78">
            <Accordion type="single" collapsible className="w-full space-y-4" data-id="element-79">
              {[{
              title: '7 noches de alojamiento en Grand Windsock Bonaire',
              content: 'Alojamiento premium en villas compartidas, diseñadas específicamente para la comodidad de los buceadores, con espacios amplios y excelente ubicación.'
            }, {
              title: 'Desayuno americano incluido',
              content: 'Comenzá cada día con energía. Desayuno completo servido todas las mañanas antes de salir a bucear.'
            }, {
              title: 'Buceos ilimitados de costa',
              content: 'Acceso a tanques las 24 horas. Podés realizar tantas inmersiones de costa como desees, a tu propio ritmo.'
            }, {
              title: '2 inmersiones embarcadas en Klein Bonaire',
              content: 'Salida especial en barco para explorar los arrecifes vírgenes de la isla deshabitada de Klein Bonaire.'
            }, {
              title: 'Nitrox incluido',
              content: 'Para buceadores certificados en Aire Enriquecido, el Nitrox está incluido sin costo adicional para mayor seguridad y tiempos de fondo.',
              extra: <div className="mt-6 rounded-xl border border-teal-500/30 bg-teal-500/5 p-5" data-id="element-80">
                      <p className="font-semibold text-teal-300 mb-2" data-id="element-81">
                        ¿No tenés certificación de Nitrox?
                      </p>
                      <p className="text-slate-300 mb-4" data-id="element-82">
                        Podés realizar la certificación directamente durante el
                        viaje en Bonaire. Una excelente oportunidad opcional
                        para aprovechar al máximo las ventajas del aire
                        enriquecido y regresar del viaje con una certificación
                        adicional.
                      </p>
                      <ul className="space-y-2 mb-4" data-id="element-83">
                        {['Material de estudio', 'Parte teórica', 'Certificación internacional'].map(c => <li key={c} className="flex items-center gap-2 text-slate-300" data-id="element-84">
                            <CheckCircle2 className="w-4 h-4 text-teal-400 flex-shrink-0" data-id="element-85" />
                            {c}
                          </li>)}
                      </ul>
                      <p className="text-sm text-slate-400" data-id="element-86">
                        Valor opcional:{' '}
                        <span className="font-semibold text-white" data-id="element-87">
                          USD 240
                        </span>
                      </p>
                    </div>
            }, {
              title: 'Vehículo para cada 4 participantes',
              content: 'Camionetas tipo pick-up ideales para cargar los equipos y moverse libremente por los puntos de buceo de la isla.'
            }, {
              title: 'Traslados aeropuerto ida y vuelta',
              content: 'Te esperamos en el aeropuerto de Bonaire y te llevamos al alojamiento, y viceversa el día de partida.'
            }, {
              title: 'BBQ grupal incluida',
              content: 'Una noche especial de asado para compartir anécdotas, relajar y fortalecer el grupo.'
            }, {
              title: 'Instructor acompañando durante todo el viaje',
              content: 'Asistencia, guía y organización constante por parte de nuestro equipo profesional para garantizar una experiencia perfecta.'
            }].map((item, i) => <AccordionItem key={i} value={`item-${i}`} className="bg-slate-800 border-slate-700 rounded-xl px-6 data-[state=open]:bg-slate-800/80 transition-colors" data-id="element-88">
                  <AccordionTrigger className="text-lg font-medium hover:no-underline py-6 text-left" data-id="element-89">
                    <div className="flex items-center gap-4" data-id="element-90">
                      <CheckCircle2 className="w-6 h-6 text-teal-400 flex-shrink-0" data-id="element-91" />
                      {item.title}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-300 text-base pb-6 pl-10" data-id="element-92">
                    {item.content}
                    {(item as {
                  content: string;
                  extra?: React.ReactNode;
                }).extra}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* NUESTRO PLAN DE EXPLORACIÓN */}
      <section className="py-24 bg-slate-950 text-white overflow-hidden" data-id="element-93">
        <div className="container mx-auto px-6 mb-12" data-id="element-94">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="max-w-3xl" data-id="element-95">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-96">
              NUESTRO PLAN DE EXPLORACIÓN
            </h2>
            <p className="text-lg text-slate-400" data-id="element-97">
              Durante la semana recorreremos las distintas caras de Bonaire.
              Cada zona ofrece paisajes submarinos únicos, diferentes especies y
              algunos de los mejores puntos de buceo del Caribe.
            </p>
          </motion.div>
        </div>

        {/* Slider de Zonas */}
        <div className="w-full pl-6 md:pl-0 md:container md:mx-auto md:px-6" data-id="element-98">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden pr-6 md:pr-0" data-id="element-99">
            {[{
            title: 'Zona Centro',
            days: 'DÍAS 1–2',
            sites: ['Bari Reef', 'Something Special', 'Front Porch'],
            img: IMAGES.whyBonaire[0]
          }, {
            title: 'Zona Sur',
            days: 'DÍAS 3–4',
            sites: ['Salt Pier', 'Alice in Wonderland', 'Hilma Hooker'],
            img: IMAGES.whyBonaire[1]
          }, {
            title: 'Zona Norte',
            days: 'DÍA 5',
            sites: ['1000 Steps', 'Karpata', 'Oil Slick Leap'],
            img: IMAGES.whyBonaire[2]
          }, {
            title: 'Klein Bonaire',
            days: 'DÍA 6',
            sites: ['Forest', "Jerry's Reef", 'Tus inmersiones favoritas del viaje'],
            img: IMAGES.whyBonaire[3]
          }].map((zone, i) => <div key={i} className="snap-center shrink-0 w-[85vw] sm:w-[60vw] md:w-[400px] h-[500px] relative rounded-3xl overflow-hidden group" data-id="element-100">
                <img src={zone.img} alt={zone.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" data-id="element-101" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/60 to-transparent" data-id="element-102" />

                <div className="absolute inset-0 p-8 flex flex-col justify-end" data-id="element-103">
                  <span className="text-teal-400 font-bold tracking-wider text-sm mb-2" data-id="element-104">
                    {zone.days}
                  </span>
                  <h3 className="text-3xl font-bold text-white mb-6" data-id="element-105">
                    {zone.title}
                  </h3>

                  <div data-id="element-106">
                    <p className="text-slate-300 text-sm uppercase tracking-wider mb-3 font-semibold" data-id="element-107">
                      Sitios destacados:
                    </p>
                    <ul className="space-y-2" data-id="element-108">
                      {zone.sites.map((site, j) => <li key={j} className="flex items-center gap-2 text-slate-200" data-id="element-109">
                          <CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0" data-id="element-110" />
                          <span data-id="element-111">{site}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </div>)}
          </motion.div>
        </div>

        <div className="container mx-auto px-6 mt-8" data-id="element-112">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-113">
            <p className="text-sm text-slate-500 italic border-l-2 border-teal-500/30 pl-4 max-w-3xl" data-id="element-114">
              Los puntos de buceo pueden variar según las condiciones climáticas
              y operativas para garantizar siempre la mejor experiencia posible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECCIÓN 8 & 9 - INVERSIÓN Y RESERVA */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-115">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-teal-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-116" />

        <div className="container mx-auto px-6 relative z-10" data-id="element-117">
          {/* Reserva primero - inversión inicial baja */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="max-w-2xl mx-auto bg-white rounded-3xl p-8 md:p-12 text-center text-slate-900 shadow-xl mb-16" data-id="element-118">
            <p className="text-sm font-semibold uppercase tracking-widest text-teal-600 mb-2" data-id="element-119">
              Reservá tu lugar
            </p>
            <div className="flex justify-center items-baseline text-5xl font-extrabold mb-6" data-id="element-120">
              <span className="text-2xl font-medium text-slate-400 mr-1" data-id="element-121">
                Seña USD
              </span>
              {CONFIG.depositAmount}
            </div>
            <p className="text-slate-600 mb-4 text-lg" data-id="element-122">
              Reservá tu lugar dentro del grupo realizando una seña equivalente
              a USD {CONFIG.depositAmount}.
            </p>
            <p className="text-slate-600 mb-4" data-id="element-123">
              Los lugares se asignan por orden de reserva y el grupo está
              limitado a {CONFIG.totalSpots} participantes.
            </p>
            <p className="text-sm text-slate-500 mb-8" data-id="element-124">
              Una vez realizada la reserva, nos pondremos en contacto para
              coordinar los siguientes pagos y responder cualquier consulta
              sobre el viaje.
            </p>
            <Button size="lg" className="w-full md:w-auto bg-teal-500 hover:bg-teal-600 text-white text-lg px-12 h-14 rounded-full" onClick={scrollToParticipante} data-id="element-125">
              🤿 Quiero sumarme al grupo
            </Button>
          </motion.div>

          {/* Inversión total del viaje */}
          <motion.div id="elegi-participante" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12 scroll-mt-24" data-id="element-126">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-127">
              Elegí cómo querés vivir Bonaire
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto" data-id="element-128">
              Viajá con la tranquilidad de tener organizados los aspectos más
              importantes de la experiencia, para que solo tengas que enfocarte
              en disfrutar Bonaire.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="max-w-3xl mx-auto" data-id="element-129">
            <p className="text-center text-slate-400 mb-6" data-id="element-130">
              Elegí tu tipo de participante para continuar con la reserva.
            </p>

            {/* Selector de participante (radio group) */}
            <div role="radiogroup" aria-label="Tipo de participante" className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-131">
              {[{
              id: 'diver' as const,
              title: 'Buzo Certificado',
              subtitle: 'Programa completo de buceo',
              price: CONFIG.priceDiver
            }, {
              id: 'companion' as const,
              title: 'Acompañante No Buceador',
              subtitle: 'Disfrutá la experiencia en superficie',
              price: CONFIG.priceNonDiver
            }].map(opt => {
              const selected = packageType === opt.id;
              return <button key={opt.id} type="button" role="radio" aria-checked={selected} onClick={() => setPackageType(opt.id)} className={`text-left rounded-2xl p-5 border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 ${selected ? 'bg-slate-800 border-teal-500 ring-2 ring-teal-500/40' : 'bg-slate-800/40 border-slate-700 hover:border-slate-500'}`} data-id="element-132">
                    <div className="flex items-start gap-3" data-id="element-133">
                      <span className={`mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${selected ? 'border-teal-400' : 'border-slate-500'}`} data-id="element-134">
                        {selected && <span className="h-2.5 w-2.5 rounded-full bg-teal-400" data-id="element-135" />}
                      </span>
                      <div data-id="element-136">
                        <p className="text-lg font-bold text-white leading-tight" data-id="element-137">
                          {opt.title}
                        </p>
                        <p className="text-sm text-slate-400 mt-0.5" data-id="element-138">
                          {opt.subtitle}
                        </p>
                      </div>
                    </div>
                  </button>;
            })}
            </div>

            {/* Detalle dinámico según selección */}
            <div className="mt-6 rounded-3xl bg-white text-slate-900 p-8 md:p-10 shadow-xl" data-id="element-139">
              {packageType === 'diver' ? <div data-id="element-140">
                  <p className="text-sm font-semibold uppercase tracking-widest text-teal-600 mb-2" data-id="element-141">
                    Buzo Certificado
                  </p>
                  <div className="flex items-baseline mb-4" data-id="element-142">
                    <span className="text-xl font-medium text-slate-400 mr-2" data-id="element-143">
                      Total USD
                    </span>
                    <span className="text-5xl font-extrabold" data-id="element-144">
                      {CONFIG.priceDiver}
                    </span>
                  </div>
                  <p className="text-slate-600 mb-8 leading-relaxed" data-id="element-145">
                    Incluye alojamiento, desayunos, programa completo de buceo,
                    Nitrox, vehículo, traslados y actividades del grupo.
                  </p>
                  <Button size="lg" className="w-full bg-teal-500 hover:bg-teal-600 text-white text-base md:text-lg font-bold uppercase tracking-wide h-14 rounded-full" onClick={() => window.location.href = CONFIG.checkoutDiverUrl} data-id="element-146">
                    Reservar como buzo
                  </Button>
                </div> : <div data-id="element-147">
                  <p className="text-sm font-semibold uppercase tracking-widest text-teal-600 mb-2" data-id="element-148">
                    Acompañante No Buceador
                  </p>
                  <div className="flex items-baseline mb-4" data-id="element-149">
                    <span className="text-xl font-medium text-slate-400 mr-2" data-id="element-150">
                      Total USD
                    </span>
                    <span className="text-5xl font-extrabold" data-id="element-151">
                      {CONFIG.priceNonDiver}
                    </span>
                  </div>
                  <p className="text-slate-600 mb-8 leading-relaxed" data-id="element-152">
                    Incluye alojamiento, desayunos, vehículo, traslados y
                    actividades grupales.
                  </p>
                  <Button size="lg" className="w-full bg-teal-500 hover:bg-teal-600 text-white text-base md:text-lg font-bold uppercase tracking-wide h-14 rounded-full" onClick={() => window.location.href = CONFIG.checkoutCompanionUrl} data-id="element-153">
                    Viajar sin bucear
                  </Button>
                </div>}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECCIÓN 4 & 5 - ALOJAMIENTO */}
      <section className="py-24 bg-slate-50" data-id="element-154">
        <div className="container mx-auto px-6" data-id="element-155">
          <div className="flex flex-col lg:flex-row gap-16 items-center mb-16" data-id="element-156">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="lg:w-1/2" data-id="element-157">
              <Badge className="bg-teal-100 text-teal-800 mb-4" data-id="element-158">
                Alojamiento Premium
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-id="element-159">
                Grand Windsock Bonaire
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed" data-id="element-160">
                Ubicado a metros del mar y de los principales puntos de buceo de
                Bonaire. Un resort diseñado pensando en las necesidades del
                buceador, combinando lujo, comodidad y practicidad.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-161">
                {['Villas amplias y luminosas', 'Jardín tropical y piscina', 'Cocina totalmente equipada', 'Espacios comunes para relajar', 'Excelente ubicación para buceadores'].map((item, i) => <li key={i} className="flex items-center gap-3 text-slate-700 font-medium" data-id="element-162">
                    <div className="w-2 h-2 bg-teal-500 rounded-full" data-id="element-163" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="lg:w-1/2 grid grid-cols-2 gap-4" data-id="element-164">
              <div className="relative col-span-2 rounded-3xl overflow-hidden group" data-id="element-165">
                <img src={IMAGES.accommodation.exterior} alt="Exterior de la villa rodeada de jardín tropical" className="w-full h-72 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-166" />
                <span className="absolute bottom-3 left-4 text-white text-sm font-medium drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]" data-id="element-167">
                  Exterior de la villa
                </span>
              </div>
              <div className="relative rounded-3xl overflow-hidden group" data-id="element-168">
                <img src={IMAGES.accommodation.terraza} alt="Terraza con lounge bajo palapa y jardín tropical" className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-169" />
                <span className="absolute bottom-3 left-4 text-white text-sm font-medium drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]" data-id="element-170">
                  Terraza & jardín
                </span>
              </div>
              <div className="relative rounded-3xl overflow-hidden group" data-id="element-171">
                <img src={IMAGES.accommodation.habitacion} alt="Habitación en base doble con vista al jardín" className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-172" />
                <span className="absolute bottom-3 left-4 text-white text-sm font-medium drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]" data-id="element-173">
                  Habitación doble
                </span>
              </div>
            </motion.div>
          </div>

          {/* Galería complementaria — espacios comunes y baño */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-16" data-id="element-174">
            <div className="relative sm:col-span-2 rounded-3xl overflow-hidden group" data-id="element-175">
              <img src={IMAGES.accommodation.living} alt="Espacios comunes amplios y luminosos con techo abovedado" className="w-full h-72 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-176" />
              <span className="absolute bottom-3 left-4 text-white text-sm font-medium drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]" data-id="element-177">
                Espacios comunes
              </span>
            </div>
            <div className="relative rounded-3xl overflow-hidden group" data-id="element-178">
              <img src={IMAGES.accommodation.bano} alt="Baño privado moderno y luminoso" className="w-full h-72 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-179" />
              <span className="absolute bottom-3 left-4 text-white text-sm font-medium drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]" data-id="element-180">
                Baño privado
              </span>
            </div>
          </motion.div>

          {/* Distribución */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-slate-100" data-id="element-181">
            <h3 className="text-2xl font-bold mb-8 text-center text-slate-900" data-id="element-182">
              Distribución Exclusiva
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-183">
              <div className="text-center" data-id="element-184">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-185">
                  <MapPin className="w-8 h-8 text-teal-600" data-id="element-186" />
                </div>
                <h4 className="font-bold text-lg mb-2" data-id="element-187">3 Villas Exclusivas</h4>
                <p className="text-slate-600 text-sm" data-id="element-188">
                  Reservadas únicamente para nuestro grupo, garantizando un
                  ambiente privado y relajado.
                </p>
              </div>
              <div className="text-center" data-id="element-189">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-190">
                  <Users className="w-8 h-8 text-teal-600" data-id="element-191" />
                </div>
                <h4 className="font-bold text-lg mb-2" data-id="element-192">Base Doble</h4>
                <p className="text-slate-600 text-sm" data-id="element-193">
                  Habitaciones organizadas en base doble y configuradas con
                  camas individuales para cada ocupante. Un espacio cómodo y
                  pensado para brindar descanso, privacidad y comodidad después
                  de cada jornada de buceo.
                </p>
              </div>
              <div className="text-center" data-id="element-194">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-195">
                  <CheckCircle2 className="w-8 h-8 text-teal-600" data-id="element-196" />
                </div>
                <h4 className="font-bold text-lg mb-2" data-id="element-197">Baño Privado</h4>
                <p className="text-slate-600 text-sm" data-id="element-198">
                  Compartido únicamente por los ocupantes de la habitación, para
                  mayor comodidad.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECCIÓN 6 - QUIÉNES SOMOS */}
      <section className="py-24 bg-white border-t border-slate-100" data-id="element-199">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-200">
          <div className="flex flex-col md:flex-row items-center gap-12 bg-slate-900 rounded-3xl p-8 md:p-12 text-white overflow-hidden relative" data-id="element-201">
            <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl" data-id="element-202" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" data-id="element-203" />

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="md:w-1/3 flex justify-center relative z-10" data-id="element-204">
              <div className="text-center md:text-left" data-id="element-205">
                <p className="text-white font-bold uppercase tracking-[0.16em] text-3xl md:text-4xl leading-tight" data-id="element-206">
                  Buceo y<br data-id="element-207" />
                  Navegación
                </p>
                <div className="mx-auto md:mx-0 mt-4 h-px w-20 bg-teal-400/60" data-id="element-208" />
                <p className="mt-4 text-xs uppercase tracking-[0.2em] text-teal-300" data-id="element-209">
                  Escuela de Buceo · Viajes Grupales · Agencia de Turismo
                </p>
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="md:w-2/3 relative z-10 text-center md:text-left" data-id="element-210">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-211">
                Organizado por Buceo y Navegación
              </h2>
              <p className="text-lg text-slate-300 mb-8" data-id="element-212">
                Más de 10 años formando buzos y organizando experiencias
                inolvidables alrededor del mundo.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left" data-id="element-213">
                {/* Tarjeta 1 - Instructor a cargo */}
                <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 h-full" data-id="element-214">
                  <p className="text-sm text-teal-400 font-semibold uppercase tracking-wider mb-2" data-id="element-215">
                    Instructor a cargo del viaje
                  </p>
                  <p className="text-xl font-bold mb-0.5" data-id="element-216">
                    Mauro Aníbal Mariscotti
                  </p>
                  <p className="text-slate-300 flex flex-wrap items-center gap-2 mb-3" data-id="element-217">
                    Instructor PADI
                    <span className="bg-slate-700 px-2 py-0.5 rounded text-xs" data-id="element-218">
                      #{CONFIG.instructorPadiNumber}
                    </span>
                  </p>
                  <p className="text-sm text-slate-400 leading-relaxed" data-id="element-219">
                    Responsable de la coordinación general del viaje,
                    actividades de buceo y acompañamiento del grupo durante toda
                    la experiencia en Bonaire.
                  </p>
                </div>

                {/* Tarjeta 2 - Asesor de viaje */}
                <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 h-full" data-id="element-220">
                  <p className="text-sm text-teal-400 font-semibold uppercase tracking-wider mb-2" data-id="element-221">
                    Asesor de viaje y atención personalizada
                  </p>
                  <p className="text-xl font-bold mb-0.5" data-id="element-222">
                    Fernando J. Alvarez
                  </p>
                  <p className="text-slate-300 flex flex-wrap items-center gap-2 mb-3" data-id="element-223">
                    Instructor PADI
                    <span className="bg-slate-700 px-2 py-0.5 rounded text-xs" data-id="element-224">
                      #342282
                    </span>
                  </p>
                  <p className="text-sm text-slate-400 leading-relaxed" data-id="element-225">
                    Disponible para responder consultas sobre el viaje,
                    reservas, certificaciones, requisitos de participación y
                    planificación previa al viaje.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 mt-8 justify-center md:justify-start" data-id="element-226">
                <Button variant="outline" className="border-slate-600 text-slate-900 bg-white hover:bg-slate-100 rounded-full" onClick={() => window.open(CONFIG.instagramUrl, '_blank')} data-id="element-227">
                  <Instagram className="w-4 h-4 mr-2" data-id="element-228" /> Seguinos en Instagram
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECCIÓN 7 - TESTIMONIOS */}
      <section className="py-24 bg-slate-50" data-id="element-229">
        <div className="container mx-auto px-6" data-id="element-230">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-231">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" data-id="element-232">
              Experiencias reales de nuestros viajeros
            </h2>
            <p className="text-lg text-slate-600" data-id="element-233">
              Experiencias reales de quienes ya viajaron con nosotros.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-234">
            {[{
            name: 'Martín G.',
            text: 'La organización fue impecable. Mauro está en todos los detalles. Volvería a viajar con ellos sin dudarlo.'
          }, {
            name: 'Laura S.',
            text: 'Bonaire es un sueño y hacerlo con este grupo lo hizo aún mejor. Excelente onda, profesionalismo y seguridad en el agua.'
          }, {
            name: 'Diego F.',
            text: 'Mi primer viaje de buceo internacional. Me sentí súper cuidado. Las villas increíbles y los buceos inolvidables.'
          }].map((testimonial, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} data-id="element-235">
                <Card className="h-full border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-236">
                  <CardHeader data-id="element-237">
                    <div className="flex text-yellow-400 mb-2" data-id="element-238">
                      {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" data-id="element-239" />)}
                    </div>
                    <CardTitle className="text-lg" data-id="element-240">
                      {testimonial.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent data-id="element-241">
                    <p className="text-slate-600 italic" data-id="element-242">
                      "{testimonial.text}"
                    </p>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>

          <div className="text-center" data-id="element-243">
            <Button variant="outline" className="rounded-full border-slate-300 text-slate-700" onClick={() => window.open(CONFIG.googleReviewsUrl, '_blank')} data-id="element-244">
              Ver más reseñas en Google
            </Button>
          </div>
        </div>
      </section>

      {/* SECCIÓN 10 - PREGUNTAS FRECUENTES */}
      <section className="py-24 bg-slate-50" data-id="element-245">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-246">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-247">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" data-id="element-248">
              Preguntas Frecuentes
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-249">
            <Accordion type="single" collapsible className="w-full bg-white rounded-2xl p-2 shadow-sm border border-slate-100" data-id="element-250">
              {([{
              q: '¿Qué certificación necesito para participar?',
              a: 'Se requiere como mínimo la certificación Open Water Diver o cualquier certificación equivalente reconocida internacionalmente por las principales organizaciones de buceo.'
            }, {
              q: '¿Puedo realizar el curso Open Water Diver en Bonaire?',
              a: <div className="space-y-3" data-id="element-251">
                        <p data-id="element-252">
                          Sí. Existe la posibilidad de realizar el curso Open
                          Water Diver directamente en Bonaire.
                        </p>
                        <p data-id="element-253">
                          La disponibilidad debe consultarse previamente antes
                          de realizar la reserva.
                        </p>
                        <p data-id="element-254">
                          El curso tiene un costo adicional de{' '}
                          <span className="font-semibold text-slate-900" data-id="element-255">
                            USD 550
                          </span>{' '}
                          e incluye:
                        </p>
                        <ul className="space-y-2" data-id="element-256">
                          {['Material de estudio', 'Entrenamiento práctico', 'Certificación internacional'].map(c => <li key={c} className="flex items-center gap-2" data-id="element-257">
                              <CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0" data-id="element-258" />
                              {c}
                            </li>)}
                        </ul>
                      </div>
            }, {
              q: '¿Cómo funcionan los pagos?',
              a: `Reserva equivalente a USD ${CONFIG.depositAmount}. Hasta 30 días antes del viaje: USD 900. Saldo restante: se abona en destino.`
            }, {
              q: '¿El viaje incluye los pasajes aéreos?',
              a: <div className="space-y-3" data-id="element-259">
                        <p data-id="element-260">No.</p>
                        <p data-id="element-261">
                          El valor del viaje no incluye los pasajes aéreos hacia
                          Bonaire. De esta manera, cada participante puede
                          elegir la aerolínea, ruta y fechas que mejor se
                          adapten a sus necesidades.
                        </p>
                        <p data-id="element-262">
                          Una vez realizada la reserva, estaremos disponibles
                          para asesorarte y ayudarte a coordinar tu viaje para
                          que puedas llegar sin inconvenientes al destino.
                        </p>
                      </div>
            }, {
              q: '¿Cómo llego a Bonaire desde Argentina?',
              a: <div className="space-y-3" data-id="element-263">
                        <p data-id="element-264">
                          Llegar a Bonaire es más fácil de lo que parece.
                          Actualmente no existen vuelos directos desde
                          Argentina, por lo que el viaje se realiza mediante una
                          conexión internacional y luego un vuelo hacia Bonaire.
                        </p>
                        <p data-id="element-265">
                          La mayoría de los viajeros suelen volar con Copa
                          Airlines vía Panamá o con Avianca vía Bogotá, aunque
                          también existen otras alternativas.
                        </p>
                        <p data-id="element-266">
                          Podés elegir el itinerario y la aerolínea que
                          prefieras, siempre que llegues a Bonaire el día de
                          inicio del viaje (30 de agosto de 2026) y regreses el
                          día de finalización (6 de septiembre de 2026).
                        </p>
                        <p data-id="element-267">
                          Nosotros coordinamos los traslados para recibirte en
                          el aeropuerto de Bonaire independientemente del
                          horario de llegada de tu vuelo, para que puedas viajar
                          con tranquilidad.
                        </p>
                        <p className="font-semibold text-slate-900" data-id="element-268">
                          ¿Necesito visa?
                        </p>
                        <p data-id="element-269">
                          Si viajás realizando escalas en Panamá, Colombia u
                          otros países de conexión habituales desde Argentina,
                          generalmente no necesitás visa.
                        </p>
                        <p data-id="element-270">
                          Las personas que vuelen a través de Estados Unidos
                          deberán verificar los requisitos migratorios
                          correspondientes (visa o autorización de ingreso
                          vigente) antes de emitir sus pasajes.
                        </p>
                        <p data-id="element-271">
                          Si tenés dudas sobre tu itinerario, podemos orientarte
                          antes de realizar la compra.
                        </p>
                      </div>
            }, {
              q: '¿Puedo sacar los vuelos por mi cuenta?',
              a: <div className="space-y-3" data-id="element-272">
                        <p data-id="element-273">
                          Sí. El paquete no exige una aerolínea específica y
                          podés comprar los vuelos que prefieras.
                        </p>
                        <p data-id="element-274">
                          Lo único importante es llegar a Bonaire el día de
                          inicio del viaje y regresar el día de finalización.
                        </p>
                        <p data-id="element-275">
                          Si lo deseás, podemos recomendarte el mismo itinerario
                          que está utilizando la mayoría del grupo para
                          facilitar las conexiones y coordinar el viaje con
                          otros participantes.
                        </p>
                      </div>
            }, {
              q: '¿Qué tasas debo pagar para viajar y bucear en Bonaire?',
              a: <div className="space-y-3" data-id="element-276">
                        <p data-id="element-277">
                          Además del valor del viaje, existen dos tasas
                          obligatorias que deben ser abonadas directamente por
                          cada participante.
                        </p>
                        <p className="font-semibold text-slate-900" data-id="element-278">
                          Visitor Entry Tax (USD 75)
                        </p>
                        <p data-id="element-279">
                          Es la tasa turística obligatoria para ingresar a
                          Bonaire. Puede abonarse online antes del viaje a
                          través del sitio oficial del Gobierno de Bonaire o
                          directamente al llegar al aeropuerto. Recomendamos
                          realizar el pago online para agilizar el ingreso al
                          destino.
                        </p>
                        <p data-id="element-280">
                          Sitio oficial:{' '}
                          <a href="https://tourismtax.bonairegov.com" target="_blank" rel="noreferrer" className="text-teal-600 underline hover:text-teal-700 break-words" data-id="element-281">
                            https://tourismtax.bonairegov.com
                          </a>
                        </p>
                        <p className="font-semibold text-slate-900" data-id="element-282">
                          Nature Fee STINAPA (USD 40)
                        </p>
                        <p data-id="element-283">
                          Es la contribución obligatoria para acceder al Parque
                          Marino de Bonaire y realizar actividades acuáticas
                          como buceo o snorkel. Puede abonarse online antes del
                          viaje o directamente en Bonaire en centros
                          habilitados.
                        </p>
                        <p data-id="element-284">
                          Sitio oficial:{' '}
                          <a href="https://stinapa.bonairenaturefee.org" target="_blank" rel="noreferrer" className="text-teal-600 underline hover:text-teal-700 break-words" data-id="element-285">
                            https://stinapa.bonairenaturefee.org
                          </a>
                        </p>
                        <p data-id="element-286">
                          Estas tasas no están incluidas en el valor del viaje y
                          deberán ser abonadas directamente por cada
                          participante.
                        </p>
                      </div>
            }, {
              q: '¿Necesito seguro de buceo?',
              a: <div className="space-y-3" data-id="element-287">
                        <p data-id="element-288">
                          El seguro de buceo no está incluido en el valor del
                          viaje.
                        </p>
                        <p data-id="element-289">
                          Si bien no suele ser un requisito obligatorio para
                          viajar a Bonaire, es altamente recomendable contar con
                          un seguro específico para actividades de buceo que
                          incluya asistencia médica y cobertura para emergencias
                          relacionadas con el buceo.
                        </p>
                        <p data-id="element-290">
                          Si todavía no contás con un seguro de buceo, podés
                          consultarnos y con gusto te recomendaremos algunas de
                          las opciones más utilizadas por nuestros viajeros.
                        </p>
                      </div>
            }, {
              q: '¿Qué gastos personales no están incluidos?',
              a: <div className="space-y-3" data-id="element-291">
                        <p data-id="element-292">
                          Los almuerzos, cenas, alquiler de equipamiento de
                          buceo, seguro de viaje, seguro de buceo, tasas
                          gubernamentales y cualquier gasto de carácter personal
                          no se encuentran incluidos en el valor del paquete.
                        </p>
                        <p data-id="element-293">
                          La mayoría de estos gastos dependen de las
                          preferencias y necesidades de cada participante.
                        </p>
                        <p data-id="element-294">
                          Si tenés dudas sobre algún gasto en particular, podés
                          consultarnos y te ayudaremos a estimarlo con mayor
                          precisión.
                        </p>
                      </div>
            }, {
              q: '¿Necesito llevar mi propio equipo?',
              a: <div className="space-y-3" data-id="element-295">
                        <p data-id="element-296">
                          El equipamiento de buceo no está incluido dentro del
                          paquete.
                        </p>
                        <p data-id="element-297">
                          Podés llevar tu propio equipo o alquilarlo
                          directamente en Bonaire. Para quienes necesiten el
                          equipo completo durante toda la semana, el valor
                          aproximado es de{' '}
                          <span className="font-semibold text-slate-900" data-id="element-298">
                            USD 300
                          </span>
                          .
                        </p>
                        <p data-id="element-299">
                          Si ya contás con alguna parte del equipamiento, el
                          costo de alquiler disminuye según los elementos que
                          necesites.
                        </p>
                        <p data-id="element-300">
                          El alquiler se abona directamente en destino en
                          dólares billete.
                        </p>
                      </div>
            }, {
              q: '¿Puedo viajar con alguien que no bucea?',
              a: '¡Sí! Existe una tarifa especial para acompañantes no buceadores, y Bonaire ofrece hermosas playas y actividades en la superficie.'
            }, {
              q: '¿Puedo viajar solo?',
              a: <div className="space-y-3" data-id="element-301">
                        <p data-id="element-302">Sí.</p>
                        <p data-id="element-303">
                          Muchos participantes viajan solos y se integran al
                          grupo durante toda la experiencia.
                        </p>
                        <p data-id="element-304">
                          Las habitaciones se organizan en base doble y nos
                          encargamos de coordinar la distribución para
                          garantizar comodidad y una excelente convivencia
                          durante el viaje.
                        </p>
                      </div>
            }, {
              q: '¿Puedo solicitar una habitación privada?',
              a: <div className="space-y-3" data-id="element-305">
                        <p data-id="element-306">Sí.</p>
                        <p data-id="element-307">
                          Podés solicitar una habitación privada con baño
                          privado abonando un adicional de{' '}
                          <span className="font-semibold text-slate-900" data-id="element-308">
                            USD 500
                          </span>{' '}
                          sobre el valor del paquete.
                        </p>
                        <p data-id="element-309">
                          La disponibilidad es limitada y se recomienda
                          consultarla antes de realizar la reserva.
                        </p>
                      </div>
            }, {
              q: '¿Cuántas personas viajan?',
              a: `Es un grupo reducido y exclusivo de máximo ${CONFIG.totalSpots} participantes, para garantizar atención personalizada.`
            }, {
              q: '¿Quién acompaña el viaje?',
              a: 'Mauro Aníbal Mariscotti, Instructor PADI Certificado, estará acompañando y coordinando al grupo durante toda la estadía.'
            }] as Array<{
              q: string;
              a: React.ReactNode;
            }>).map((faq, i) => <AccordionItem key={i} value={`faq-${i}`} className="border-b border-slate-100 last:border-0 px-4" data-id="element-310">
                  <AccordionTrigger className="text-left font-semibold text-slate-800 hover:text-teal-600 py-5" data-id="element-311">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-5" data-id="element-312">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* SECCIÓN FINAL */}
      <section className="relative py-32 bg-slate-900 flex items-center justify-center overflow-hidden" data-id="element-313">
        <div className="absolute inset-0 z-0" data-id="element-314">
          <img src={IMAGES.finalCta} alt="Bonaire Landscape" className="w-full h-full object-cover opacity-40" data-id="element-315" />
          <div className="absolute inset-0 bg-slate-900/60" data-id="element-316" />
        </div>

        <div className="relative z-10 container mx-auto px-6 text-center text-white" data-id="element-317">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="max-w-3xl mx-auto" data-id="element-318">
            <h2 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-319">
              ¿Te gustaría vivir esta experiencia?
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-320">
              <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 h-14 rounded-full" onClick={scrollToParticipante} data-id="element-321">
                🤿 Quiero sumarme al grupo
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900 text-lg px-8 h-14 rounded-full bg-transparent" onClick={() => window.open(whatsappLink, '_blank')} data-id="element-322">
                Hablar por WhatsApp
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER SIMPLE — Marca tipográfica */}
      <footer className="bg-slate-950 py-12 text-center text-slate-500 text-sm" data-id="element-323">
        <div className="container mx-auto px-6" data-id="element-324">
          <p className="text-white font-semibold uppercase tracking-[0.22em] text-xl md:text-2xl mb-2" data-id="element-325">
            Buceo y Navegación
          </p>
          <p className="text-[11px] uppercase tracking-[0.2em] text-teal-300 mb-6" data-id="element-326">
            Escuela de Buceo · Viajes Grupales · Agencia de Turismo
          </p>
          <p data-id="element-327">© 2026 Buceo y Navegación. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>;
}